<script setup>
import { router, useForm } from '@inertiajs/vue3'
import TextInput from '@/Shared/TextInput.vue'
import ConfirmModal from '@/Shared/ConfirmModal.vue'
import Icon from '@/Shared/Icon.vue'
import { useDate } from '@/Composables/useDate'
import { ref } from 'vue'
import LoadingButton from '@/Shared/LoadingButton.vue'
import { useConfirm } from '@/Composables/useConfirm'
import Portrait from '@/Shared/Portrait.vue'

const props = defineProps({
  record: {
    type: Object,
    required: true
  },
  students: {
    type: Array,
    default: () => []
  }
})

const showStudents = ref(false)

async function remove(student) {
  if (await useConfirm('¿Estas seguro de que quieres eliminar el alumno de esta clase?')) {
    router.delete(`/lessons/records/${props.record.id}/student/${student.id}/delete`)
  }
}

let form = useForm({
  date_start: useDate(props.record.date_start).format('YYYY-MM-DD'),
  time_start: useDate(props.record.date_start).format('HH:mm'),
  time_end: useDate(props.record.date_end).format('HH:mm')
})

function update() {
  form.put(`/lessons/records/${props.record.id}/update`)
}

let formNotify = useForm({
  students: props.students.map((s) => s.id)
})

function submit() {
  formNotify.post(`/lessons/records/${props.record.id}/students/notify`, {
    preserveState: false
  })
}

const breadcrumbs = [
  { label: 'Clases', href: '/lessons' },
  {
    label: `${props.record.lesson.level.name} ${props.record.lesson.time_start} - ${props.record.lesson.time_end}`,
    href: `/lessons/${props.record.lesson.id}/edit`
  },
  { label: `${useDate(props.record.date_start).format('DD-MM-YYYY')}` }
]
</script>

<template>
  <Portrait
    :title="`Registro: ${record.lesson.level.name}`"
    :subtitle="`${useDate(record.date_start).format('DD-MM-YYYY')} | ${useDate(
      record.date_start
    ).format('HH:mm')} - ${useDate(record.date_end).format('HH:mm')}`"
    :breadcrumbs="breadcrumbs"
    :footer-actions="true"
  >
    <ConfirmModal
      v-if="showStudents"
      :loading="formNotify.processing"
      @close="showStudents = false"
      @confirm="submit"
    >
      <template #title> Selecciona los alumnos </template>
      <template #body>
        <div class="text-sm mt-4">Selecciona a los alumnos a los que enviar una notificación.</div>
        <div class="overflow-y-auto" style="max-height: 70vh">
          <div v-for="(student, index) in students" :key="index">
            <label class="flex items-center mt-6 select-none">
              <input
                v-model="formNotify.students"
                :value="student.id"
                class="mr-1"
                type="checkbox"
              />
              <span class="text-sm">{{ student.player.fullname }}</span>
            </label>
          </div>
        </div>
      </template>
    </ConfirmModal>

    <!-- Información del registro -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden p-6 mb-6">
      <div class="flex items-center mb-6 pb-4 border-b border-gray-100">
        <div
          class="h-16 w-16 rounded-full flex items-center justify-center text-white font-bold mr-4"
          :style="`background-color: ${record.lesson.level.color || '#4F46E5'};`"
        >
          {{ record.lesson.level.name.charAt(0) }}
        </div>
        <div>
          <h3 class="text-lg font-medium text-gray-900">{{ record.lesson.level.name }}</h3>
          <p class="text-sm text-gray-500">Entrenador: {{ record.lesson.coach.player.fullname }}</p>
        </div>
      </div>

      <form class="space-y-6" @submit.prevent="update">
        <div class="grid grid-cols-1 gap-6">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <TextInput v-model="form.date_start" type="date" class="w-full" label="Día" />
            <TextInput v-model="form.time_start" type="time" class="w-full" label="Hora inicio" />
            <TextInput v-model="form.time_end" type="time" class="w-full" label="Hora fin" />
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextInput
              disabled
              :model-value="record.lesson.coach.player.fullname"
              class="w-full"
              label="Entrenador"
            />
            <TextInput
              disabled
              :model-value="props.record.lesson.level.name"
              class="w-full"
              label="Nivel"
            />
          </div>
        </div>
      </form>
    </div>

    <!-- Sección de Alumnos -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
      <div class="p-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
        <h3 class="text-lg font-medium text-gray-900">Alumnos</h3>
        <button type="button" class="btn-indigo" @click="showStudents = !showStudents">
          <span class="flex items-center">
            <Icon name="Bell" class="h-5 w-5 mr-2" />
            <span>Notificar</span>
          </span>
        </button>
      </div>

      <div class="p-6">
        <div class="grid sm:grid-cols-2 gap-4">
          <div
            v-for="(student, index) in record.students"
            :key="index"
            class="flex justify-between items-center p-3 border border-gray-200 rounded-lg hover:border-indigo-300 transition-colors"
          >
            <span class="text-gray-800 font-medium">
              {{ student.player.fullname }}
            </span>

            <button
              class="text-gray-400 hover:text-red-500 transition-colors"
              @click="remove(student)"
            >
              <Icon class="w-5 h-5" name="Trash" />
            </button>
          </div>
          <div v-if="!record.students.length" class="col-span-2 text-center text-gray-500 py-6">
            No hay alumnos todavía.
          </div>
        </div>
      </div>
    </div>

    <!-- Sección de Solicitudes -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div class="p-4 border-b border-gray-200 bg-gray-50">
        <h3 class="text-lg font-medium text-gray-900">Solicitudes</h3>
      </div>

      <div class="p-6 space-y-4">
        <div
          v-for="note in record.notes"
          :key="note.id"
          class="p-4 border border-gray-100 rounded-lg"
        >
          <div class="leading-6">
            <p class="text-gray-800" v-html="note.message" />
            <span class="text-sm text-gray-500 mt-2 block">
              {{ useDate(note.created_at).diffForHumans() }}
            </span>
          </div>
        </div>
        <div v-if="!record.notes.length" class="text-center text-gray-500 py-6">
          No hay solicitudes pendientes.
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-end w-full">
        <LoadingButton :loading="form.processing" class="btn-indigo" @click="update">
          Guardar cambios
        </LoadingButton>
      </div>
    </template>
  </Portrait>
</template>
